import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import ahoy from "../ahoy_config"

// Le fichier d'un livre part vers le stockage DÈS qu'il est choisi, pendant
// que le créateur remplit le reste. Le formulaire n'emporte plus que la
// référence signée du fichier (champ caché), jamais le fichier lui-même.
//
// Pourquoi (ticket #4, 23/10/2026) : le PDF voyageait dans le formulaire,
// Cloudflare (offre Free) refuse tout corps de plus de 100 Mo, et le créateur
// retombait sur un formulaire vide, sans un mot. Reproduit sur staging :
// 95 Mo passe, 105 Mo rend 413. Deux créateurs bloqués, neuf jours sans
// que personne ne le sache.
//
// Ce qu'il tient :
//   • la limite est annoncée, et contrôlée AVANT tout envoi ;
//   • une barre par fichier, et le texte « vous pouvez continuer » ;
//   • « Publier » pendant un envoi attend la fin, puis part tout seul ;
//   • un échec laisse la page intacte et propose « Réessayer » ;
//   • un jeton de session périmé se renouvelle sans recharger la page ;
//   • chaque envoi est mesuré (ahoy « Envoi de fichier »), réussi ou non.
//
// Un contrôleur par édition (bloc « Ce que le client reçoit »). Le formulaire
// est partagé : l'écoute de sa soumission est posée une fois, sur lui.
const TYPES = { pdf: "application/pdf", epub: "application/epub+zip", zip: "application/zip" }

export default class extends Controller {
  static targets = ["input", "liste", "etat", "signes"]
  static values = { url: String, champ: String, maxOctets: Number, jetonUrl: String, connexionUrl: String }

  connect() {
    this.fichiers = new Map()
    const form = this.element.closest("form")
    if (form && !form.dataset.fichierLivreEcoute) {
      form.dataset.fichierLivreEcoute = "1"
      form.addEventListener("submit", retenirLaSoumission, true)
    }
  }

  choisir() {
    const fichiers = Array.from(this.inputTarget.files || [])
    // Le champ natif n'a pas de name : rien ne part avec le formulaire. On le
    // vide pour que le même fichier puisse être rechoisi après un échec.
    this.inputTarget.value = ""
    fichiers.forEach((f) => this.envoyer(typer(f)))
  }

  envoyer(fichier) {
    const li = this.ligne(fichier)
    if (this.maxOctetsValue && fichier.size > this.maxOctetsValue) {
      this.marquer(li, "erreur", `${fichier.name} fait ${taille(fichier.size)}. La limite est de ${taille(this.maxOctetsValue)} par fichier. Compressez-le ou coupez-le en deux parties.`)
      this.mesurer("refuse_taille", fichier, 0)
      return
    }
    this.lancer(li, fichier)
  }

  lancer(li, fichier, { jetonRenouvele = false } = {}) {
    const debut = performance.now()
    this.marquer(li, "en-cours", `Envoi de ${fichier.name} : 0 % (0 sur ${taille(fichier.size)}). Vous pouvez continuer à remplir la page.`)
    this.barre(li, 0)
    const upload = new DirectUpload(fichier, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (e) => {
          if (!e.lengthComputable) return
          const pct = Math.round((e.loaded * 100) / e.total)
          this.barre(li, pct)
          this.texte(li, `Envoi de ${fichier.name} : ${pct} % (${taille(e.loaded)} sur ${taille(e.total)}). Vous pouvez continuer à remplir la page.`)
          rafraichirLeBouton(this.element.closest("form"))
        })
      }
    })
    upload.create(async (erreur, blob) => {
      if (erreur && sessionRefusee(erreur)) {
        this.mesurer("echec", fichier, debut, String(erreur))
        // 03/11/2026 : 19 envois refusés pour un jeton de session périmé
        // entre le 29 et le 31/10. Si la personne est encore connectée (onglet
        // ouvert avant sa dernière connexion), un jeton neuf suffit : on relance
        // une fois, sans rien lui demander. Sinon, il faut se reconnecter.
        if (!jetonRenouvele && await this.renouvelerLeJeton()) {
          this.lancer(li, fichier, { jetonRenouvele: true })
          return
        }
        this.marquerDeconnecte(li, fichier, String(erreur))
        this.soumettreSiAttendu()
        return
      }
      if (erreur) {
        this.marquer(li, "erreur", `L'envoi de ${fichier.name} n'est pas arrivé. Rien n'est perdu sur cette page.`, { reessayer: true, detail: String(erreur) })
        this.mesurer("echec", fichier, debut, String(erreur))
        this.soumettreSiAttendu()
        return
      }
      const cache = document.createElement("input")
      cache.type = "hidden"
      cache.name = this.champValue
      cache.value = blob.signed_id
      cache.dataset.fichierLivreSigne = "1"
      li.dataset.signedId = blob.signed_id
      this.signesTarget.appendChild(cache)
      this.marquer(li, "pret", `${fichier.name} · ${taille(fichier.size)} · prêt`, { retirer: true })
      this.mesurer("ok", fichier, debut)
      this.element.dispatchEvent(new CustomEvent("fichier-livre:change", { bubbles: true }))
      this.soumettreSiAttendu()
    })
  }

  async reessayer(event) {
    const li = event.target.closest(".bv-envoi")
    const fichier = this.fichiers.get(li)
    if (!fichier) return
    if (li.dataset.deconnecte === "1") {
      // Reconnecté dans un autre onglet : cette page porte encore l'ancien
      // jeton. Sans le nouveau, l'envoi échouerait encore, pour rien.
      if (!(await this.renouvelerLeJeton())) {
        this.marquerDeconnecte(li, fichier, li.title)
        return
      }
      delete li.dataset.deconnecte
    }
    this.lancer(li, fichier)
  }

  // Demande le jeton de la session actuelle et le pose partout où la page en
  // porte un : la balise lue par l'envoi, et les formulaires, sans quoi
  // « Publier » échouerait à son tour. Faux si la personne n'est plus connectée.
  async renouvelerLeJeton() {
    if (!this.jetonUrlValue) return false
    try {
      const reponse = await fetch(this.jetonUrlValue, {
        headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store"
      })
      if (!reponse.ok) return false
      const { jeton } = await reponse.json()
      if (!jeton) return false
      document.querySelector('meta[name="csrf-token"]')?.setAttribute("content", jeton)
      document.querySelectorAll('input[name="authenticity_token"]').forEach((i) => { i.value = jeton })
      return true
    } catch (_) {
      return false
    }
  }

  marquerDeconnecte(li, fichier, detail) {
    li.dataset.deconnecte = "1"
    this.marquer(li, "erreur",
      `Votre session a expiré, ${fichier.name} n'est pas parti. Reconnectez-vous dans l'onglet qui s'ouvre, puis revenez ici et cliquez sur Réessayer. Ce que vous avez rempli sur cette page reste là.`,
      { reessayer: true, reconnecter: true, detail })
  }

  retirer(event) {
    const li = event.target.closest(".bv-envoi")
    if (li.dataset.signedId) {
      this.signesTarget.querySelectorAll(`input[value="${CSS.escape(li.dataset.signedId)}"]`).forEach((i) => i.remove())
    }
    this.fichiers.delete(li)
    li.remove()
    this.element.dispatchEvent(new CustomEvent("fichier-livre:change", { bubbles: true }))
  }

  // ─── Le DOM d'une ligne : nom, barre, état, bouton ──────────────────────

  ligne(fichier) {
    const li = document.createElement("li")
    li.className = "bv-envoi"
    const texte = document.createElement("span")
    texte.className = "bv-envoi-texte"
    const barre = document.createElement("span")
    barre.className = "bv-barre"
    barre.setAttribute("role", "progressbar")
    barre.setAttribute("aria-valuemin", "0")
    barre.setAttribute("aria-valuemax", "100")
    const remplissage = document.createElement("span")
    remplissage.className = "bv-barre-remplie"
    barre.appendChild(remplissage)
    const actions = document.createElement("span")
    actions.className = "bv-envoi-actions"
    li.append(texte, barre, actions)
    this.listeTarget.appendChild(li)
    this.fichiers.set(li, fichier)
    return li
  }

  marquer(li, etat, message, { reessayer = false, retirer = false, reconnecter = false, detail = null } = {}) {
    li.dataset.etat = etat
    this.texte(li, message)
    const actions = li.querySelector(".bv-envoi-actions")
    actions.replaceChildren()
    if (reconnecter && this.connexionUrlValue) actions.appendChild(this.lienDeConnexion())
    if (reessayer) actions.appendChild(this.bouton("Réessayer", "fichier-livre#reessayer"))
    if (retirer) actions.appendChild(this.bouton("Retirer", "fichier-livre#retirer"))
    if (detail) li.title = detail
    if (this.hasEtatTarget) this.etatTarget.textContent = message
  }

  bouton(libelle, action) {
    const b = document.createElement("button")
    b.type = "button"
    b.className = "bv-envoi-bouton"
    b.textContent = libelle
    b.dataset.action = action
    return b
  }

  lienDeConnexion() {
    const a = document.createElement("a")
    a.className = "bv-envoi-bouton"
    a.href = this.connexionUrlValue
    a.target = "_blank"
    a.rel = "noopener"
    a.textContent = "Me reconnecter"
    return a
  }

  texte(li, message) {
    li.querySelector(".bv-envoi-texte").textContent = message
  }

  barre(li, pct) {
    li.dataset.pourcent = pct
    const barre = li.querySelector(".bv-barre")
    barre.setAttribute("aria-valuenow", pct)
    barre.querySelector(".bv-barre-remplie").style.width = `${pct}%`
  }

  mesurer(resultat, fichier, debut, erreur = null) {
    try {
      ahoy.track("Envoi de fichier", {
        resultat, octets: fichier.size, type: fichier.type,
        ms: debut ? Math.round(performance.now() - debut) : 0,
        erreur: erreur ? erreur.slice(0, 200) : null,
        path: window.location.pathname
      })
    } catch (_) { /* la mesure ne casse jamais l'envoi */ }
  }

  soumettreSiAttendu() {
    const form = this.element.closest("form")
    if (!form || form.dataset.fichierLivreAttente !== "1") return
    if (envoisEnCours(form) > 0) { rafraichirLeBouton(form); return }
    delete form.dataset.fichierLivreAttente
    const bouton = form.__fichierLivreBouton
    rendreLesBoutons(form)
    if (form.querySelector(".bv-envoi[data-etat='erreur']")) return // la personne décide : réessayer ou retirer
    if (bouton && bouton.isConnected) form.requestSubmit(bouton)
    else form.requestSubmit()
  }
}

// ─── Le formulaire : « Publier » pendant un envoi attend la fin ─────────────

function retenirLaSoumission(event) {
  const form = event.currentTarget
  if (envoisEnCours(form) === 0) return
  event.preventDefault()
  event.stopImmediatePropagation()
  form.dataset.fichierLivreAttente = "1"
  form.__fichierLivreBouton = event.submitter || null
  form.querySelectorAll("button[type=submit], input[type=submit], button:not([type])").forEach((b) => {
    if (b.dataset.fichierLivreOriginal == null) b.dataset.fichierLivreOriginal = b.tagName === "INPUT" ? b.value : b.innerHTML
    b.disabled = true
    b.setAttribute("aria-busy", "true")
  })
  rafraichirLeBouton(form)
}

function rafraichirLeBouton(form) {
  if (!form || form.dataset.fichierLivreAttente !== "1") return
  const b = form.__fichierLivreBouton
  if (!b) return
  const publie = b.value === "published" || b.name === "publish_after_save"
  const libelle = `${publie ? "Publication" : "Enregistrement"} à la fin de l'envoi (${pourcentGlobal(form)} %)`
  if (b.tagName === "INPUT") b.value = libelle
  else b.textContent = libelle
}

function rendreLesBoutons(form) {
  form.querySelectorAll("[data-fichier-livre-original]").forEach((b) => {
    if (b.tagName === "INPUT") b.value = b.dataset.fichierLivreOriginal
    else b.innerHTML = b.dataset.fichierLivreOriginal
    delete b.dataset.fichierLivreOriginal
    b.disabled = false
    b.removeAttribute("aria-busy")
  })
}

// L'envoi direct d'Active Storage rend « Status: 401 » quand le serveur refuse
// la requête faute de session ou de jeton valide (ApplicationController).
function sessionRefusee(erreur) {
  return /Status: 401\b/.test(String(erreur))
}

function envoisEnCours(form) {
  return form.querySelectorAll(".bv-envoi[data-etat='en-cours']").length
}

function pourcentGlobal(form) {
  const lignes = Array.from(form.querySelectorAll(".bv-envoi[data-etat='en-cours']"))
  if (lignes.length === 0) return 100
  return Math.round(lignes.reduce((s, li) => s + Number(li.dataset.pourcent || 0), 0) / lignes.length)
}

// Un navigateur qui ne connaît pas l'extension rend un type vide, que le
// serveur refuse. On le déduit de l'extension pour les trois formats acceptés.
function typer(f) {
  if (f.type) return f
  const ext = (f.name.split(".").pop() || "").toLowerCase()
  return TYPES[ext] ? new File([f], f.name, { type: TYPES[ext], lastModified: f.lastModified }) : f
}

function taille(octets) {
  if (octets >= 1024 * 1024 * 1024) return `${(octets / 1024 / 1024 / 1024).toFixed(1)} Go`
  const mo = octets / 1024 / 1024
  if (mo >= 1) return `${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo`
  return `${Math.max(1, Math.round(octets / 1024))} Ko`
}
